import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Phone, Mail, MessageCircle, Calendar, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { clientStatusLabels, interactionTypeLabels, getWhatsAppLink } from '../../lib/utils';

export default function ClientForm() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [showInteractionDialog, setShowInteractionDialog] = useState(false);
  const [newInteraction, setNewInteraction] = useState({ type: 'note', description: '' });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    tags: [],
    notes: '',
    source: '',
    preferences: {
      transaction_types: [],
      property_types: [],
      neighborhoods: [],
      budget_min: null,
      budget_max: null,
      bedrooms_min: null,
      bathrooms_min: null
    },
    status: 'new'
  });

  const fetchClient = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [clientRes, interactionsRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/interactions/${id}`)
      ]);
      setFormData(clientRes.data);
      setInteractions(interactionsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar cliente.');
      navigate('/admin/clients');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePreferenceChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Nome é obrigatório.');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/clients/${id}`, formData);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/clients', formData);
        toast.success('Cliente cadastrado com sucesso!');
      }
      navigate('/admin/clients');
    } catch (error) {
      toast.error('Erro ao salvar cliente.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddInteraction = async () => {
    if (!newInteraction.description) {
      toast.error('Descrição é obrigatória.');
      return;
    }
    try {
      await api.post('/interactions', {
        client_id: id,
        type: newInteraction.type,
        description: newInteraction.description
      });
      toast.success('Interação registrada!');
      setNewInteraction({ type: 'note', description: '' });
      setShowInteractionDialog(false);
      fetchClient();
    } catch (error) {
      toast.error('Erro ao registrar interação.');
    }
  };

  const statuses = ['new', 'contacted', 'visit_scheduled', 'proposal', 'closed_won', 'closed_lost'];
  const interactionTypes = ['call', 'whatsapp', 'visit', 'proposal', 'note'];
  const sources = ['site', 'indicação', 'WhatsApp', 'Facebook', 'Instagram', 'presencial', 'outro'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/clients')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
            {isEditing ? formData.name : 'Novo Cliente'}
          </h1>
          {isEditing && (
            <Badge className="mt-1">{clientStatusLabels[formData.status]}</Badge>
          )}
        </div>
        {isEditing && formData.phone && (
          <Button asChild variant="outline" className="text-green-600">
            <a href={getWhatsAppLink(formData.phone)} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </a>
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Informações do Cliente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      data-testid="client-name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Nome completo"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="(45) 99999-9999"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="email@exemplo.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => (
                          <SelectItem key={s} value={s}>{clientStatusLabels[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Origem</Label>
                    <Select value={formData.source} onValueChange={(v) => handleChange('source', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Como conheceu?" />
                      </SelectTrigger>
                      <SelectContent>
                        {sources.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Observações</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Informações relevantes sobre o cliente..."
                    rows={3}
                    className="mt-1"
                  />
                </div>

                {/* Preferences */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-4">Preferências de Imóvel</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Orçamento Mínimo</Label>
                      <Input
                        type="number"
                        value={formData.preferences.budget_min || ''}
                        onChange={(e) => handlePreferenceChange('budget_min', e.target.value ? Number(e.target.value) : null)}
                        placeholder="R$ 0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Orçamento Máximo</Label>
                      <Input
                        type="number"
                        value={formData.preferences.budget_max || ''}
                        onChange={(e) => handlePreferenceChange('budget_max', e.target.value ? Number(e.target.value) : null)}
                        placeholder="R$ 0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Quartos (mínimo)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.preferences.bedrooms_min || ''}
                        onChange={(e) => handlePreferenceChange('bedrooms_min', e.target.value ? parseInt(e.target.value) : null)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Banheiros (mínimo)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.preferences.bathrooms_min || ''}
                        onChange={(e) => handlePreferenceChange('bathrooms_min', e.target.value ? parseInt(e.target.value) : null)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-4 mt-6">
              <Button type="button" variant="outline" onClick={() => navigate('/admin/clients')}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-brand-blue hover:bg-brand-blue-dark" data-testid="save-client-btn">
                {saving ? 'Salvando...' : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? 'Atualizar' : 'Cadastrar'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Interactions Timeline */}
        {isEditing && (
          <div>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Histórico de Interações</CardTitle>
                <Dialog open={showInteractionDialog} onOpenChange={setShowInteractionDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Nova
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Registrar Interação</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Tipo</Label>
                        <Select 
                          value={newInteraction.type} 
                          onValueChange={(v) => setNewInteraction({ ...newInteraction, type: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {interactionTypes.map((t) => (
                              <SelectItem key={t} value={t}>{interactionTypeLabels[t]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Descrição</Label>
                        <Textarea
                          value={newInteraction.description}
                          onChange={(e) => setNewInteraction({ ...newInteraction, description: e.target.value })}
                          placeholder="Descreva a interação..."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <Button onClick={handleAddInteraction} className="w-full bg-brand-blue hover:bg-brand-blue-dark">
                        Registrar
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {interactions.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Nenhuma interação registrada.</p>
                ) : (
                  <div className="space-y-4">
                    {interactions.map((interaction) => (
                      <div key={interaction.id} className="border-l-2 border-brand-blue/30 pl-4 pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {interactionTypeLabels[interaction.type]}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {new Date(interaction.date).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{interaction.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
